import React, { useEffect, useState } from "react";
import { Link, useParams /*, useNavigate*/ } from "react-router-dom";
import { nftService } from '../../../services';
import type { NFTMetadata } from '../../../services';
import "./PicturePage.css";
import { ColorRing } from "react-loader-spinner";
import openseaIcon from "../../../assets/opensea-icon.svg";
import etherscanIcon from "../../../assets/etherscan-logo.svg";
import { NFT, NFTOffer } from "../../../services/models/NFT";
import MakeOffer from "./makeOffer/MakeOffer";
import { useAccount } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import OfferList from "./offerList/OfferList";
import PicturePageHeader from "./header/PicturePageHeader";
import { useEnsName } from "wagmi";

type MessageType = 'success' | 'error' | 'info';

interface PopupProps {
  message: string;
  messageType: MessageType;
}

const Popup: React.FC<PopupProps> = ({
  message,
  messageType,
}) => { 
  return (
    <div className={`popup ${messageType}`}>
      {messageType === 'success' ? (
        <>
          <svg className="icon" viewBox="0 0 24 24">
            <path fill="currentColor" d="M12 0C5.371 0 0 5.371 0 12s5.371 12 12 12 12-5.371 12-12S18.629 0 12 0zm-1.707 15.293l-3.586-3.586 1.414-1.414L10.293 13l5.293-5.293 1.414 1.414-6.707 6.707z" />
          </svg>
          <p className="popup-text">{message}</p>
        </>
      ) : messageType === 'error' ? (
        <>
          <svg className="icon" viewBox="0 0 24 24">
            <path fill="currentColor" d="M12 0C5.371 0 0 5.371 0 12s5.371 12 12 12 12-5.371 12-12S18.629 0 12 0zm1 17h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <p>{message}</p>
        </>
      ) : (
        <>
          <svg className="icon" viewBox="0 0 24 24">
            <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <p className="popup-text">{message}</p>
        </>
      )}
    </div>
  );
};

// Image Overlay Component
const ImageOverlay: React.FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  imageUrl: string; 
  imageName: string; 
}> = ({ isOpen, onClose, imageUrl, imageName }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="image-overlay" onClick={handleOverlayClick}>
      <div className="image-overlay-content">
        <button className="image-overlay-close" onClick={onClose}>
          ×
        </button>
        <img 
          src={imageUrl} 
          alt={imageName} 
          className="image-overlay-img"
        />
      </div>
    </div>
  );
};

interface AddressLinkProps {
  showAddress: string | null | undefined;
  address: string | undefined;
}

const AddressLink = ({ showAddress, address }: AddressLinkProps) => {
  const etherscanUrl = `https://etherscan.io/address/${address}`;

  return (
    <a href={etherscanUrl} className="nftOwnerLink"target="_blank" rel="noopener noreferrer">
            <div className="marquee">
        <span>{showAddress}</span>
      </div>
    </a>
  );
};

// New component for the gray placeholder
const OwnerPlaceholder = () => {
  return (
    <div className="ownerPlaceholder">
      <div className="placeholderBar"></div>
    </div>
  );
};

const PicturePage = () => {
  const routeParams = useParams();
  //const navigate = useNavigate(); // Disabled - only used for print functionality
  const [loading, setLoading] = useState(true);
  const [ownerLoading, setOwnerLoading] = useState(true);
  const [etherscanURL, setEtherscanURL] = useState(false);
  const [selectedNFT, setSelectedNFT] = useState<NFT | undefined>();
  const [selectedNFTOffers, setSelectedNFTOffers] = useState<NFTOffer[] | undefined>();
  const [selectedNFTOwner, setSelectedNFTOwner] = useState<`0x${string}`| undefined>();
  const [isMakeOfferModalOpen, setMakeOfferModalOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<MessageType>('success');
  const [isImageOverlayOpen, setIsImageOverlayOpen] = useState(false);
  const { isConnected } = useAccount();
  
  const { open } = useAppKit();

  // Mouse-following zoom effect
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const wrapper = e.currentTarget;
    const rect = wrapper.getBoundingClientRect();
    const img = wrapper.querySelector('.picture') as HTMLImageElement;
    const lens = wrapper.querySelector('.magnify-lens') as HTMLDivElement;
    
    if (lens && img) {
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Ensure lens is visible
      lens.style.opacity = '1';
      
      // Position the lens to follow the mouse
      lens.style.left = `${x - 75}px`;
      lens.style.top = `${y - 75}px`;
      
      // Set the zoomed image as background
      lens.style.backgroundImage = `url(${img.src})`;
      lens.style.backgroundSize = `${rect.width * 2}px ${rect.height * 2}px`;
      lens.style.backgroundPosition = `-${x * 2 - 75}px -${y * 2 - 75}px`;
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const lens = e.currentTarget.querySelector('.magnify-lens') as HTMLDivElement;
    if (lens) {
      lens.style.opacity = '1';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    const lens = e.currentTarget.querySelector('.magnify-lens') as HTMLDivElement;
    if (lens) {
      lens.style.opacity = '0';
    }
  };

  // Convert NFTMetadata to NFT format
  const convertToNFTFormat = (nftMetadata: NFTMetadata): NFT => ({
    identifier: nftMetadata.identifier,
    name: nftMetadata.name,
    collection: nftMetadata.collection,
    contract: nftMetadata.contract,
    description: nftMetadata.description,
    image_url: nftMetadata.display_image_url || nftMetadata.image_url
  });

  useEffect(() => {
    const fetchNFTData = async () => {
      try {
        // Fetch all NFTs from ColorFuel API and find the matching one
        const nftMetadataList = await nftService.getNFTs();
        const nftMetadata = nftMetadataList.find((nft) => nft.name === routeParams.name);
  
        if (nftMetadata) {
          const nft = convertToNFTFormat(nftMetadata);
          setSelectedNFT(nft);
          
          // Fetch offers using the new ColorFuel API (use cache if available)
          try {
            const offers = await nftService.getOffersByNFT(nft.contract, nft.identifier, false);
            // Convert ColorFuel offers to the expected format
            const convertedOffers = offers.map(offer => ({
              current_price: offer.current_price,
              expiration_time: offer.expiration_time, // Unix timestamp in seconds (OfferList will convert to ms)
              offerer: offer.maker.address
            }));
            setSelectedNFTOffers(convertedOffers);
          } catch (error) {
            console.error('Error fetching offers:', error);
            setSelectedNFTOffers([]);
          }
          
          // Check if the collection matches specific types
          if (
            nft.collection === 'colorfuel-universal' ||
            nft.collection === 'colorfuel-primer' ||
            nft.collection === 'colorfuel-nostalgia'
          ) {
            setEtherscanURL(true);
          }
          setLoading(false);
          
          // Fetch NFT owner using ColorFuel API
          setOwnerLoading(true);
          try {
            const nftOwner = await nftService.getNFTOwner(nft.contract, nft.identifier);
            if (nftOwner) {
              setSelectedNFTOwner(nftOwner.ownerWalletAddress as `0x${string}`);
            }
          } catch (error) {
            console.error('Error fetching NFT owner:', error);
          } finally {
            setOwnerLoading(false);
          }
        }
      } catch (error) {
        console.error('Error fetching NFT data:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchNFTData();
  }, [routeParams.name]);

  const { data: ensName } = useEnsName({
    address: selectedNFTOwner || undefined, // Pass undefined if the address is null
  });

  // Rate limiting for offers refresh - 5 per minute
  const offersRefreshRateLimit = React.useRef<{
    lastRefreshes: number[],
    pending: boolean
  }>({
    lastRefreshes: [],
    pending: false
  });

  // Check offers refresh rate limit
  const checkOffersRefreshRateLimit = (): boolean => {
    const tracker = offersRefreshRateLimit.current;
    const now = Date.now();
    
    // Remove refreshes older than 1 minute
    tracker.lastRefreshes = tracker.lastRefreshes.filter(time => now - time < 60000);
    
    // Check if we've made 5 or more refreshes in the last minute
    if (tracker.lastRefreshes.length >= 5 || tracker.pending) {
      setPopupMessage("Rate limit reached. You can refresh offers 5 times per minute. Please try again later.");
      setMessageType('info');
      
      // Clear toast after 5 seconds
      setTimeout(() => {
        setPopupMessage(null);
      }, 5000);
      
      return false;
    }
    
    // Add current refresh to the tracker
    tracker.lastRefreshes.push(now);
    tracker.pending = true;
    
    return true;
  };

  // Clear offers refresh pending flag
  const clearOffersRefreshPendingFlag = () => {
    offersRefreshRateLimit.current.pending = false;
  };

  // Manual refresh offers function
  const handleRefreshOffers = async () => {
    if (!selectedNFT) return;
    
    // Check rate limit before proceeding
    if (!checkOffersRefreshRateLimit()) {
      return;
    }
    
    try {
      const offers = await nftService.getOffersByNFT(selectedNFT.contract, selectedNFT.identifier, true);
      
      const convertedOffers = offers.map(offer => ({
        current_price: offer.current_price,
        expiration_time: offer.expiration_time,
        offerer: typeof offer.maker === 'string' ? offer.maker : offer.maker?.address || ''
      }));
      
      setSelectedNFTOffers(convertedOffers);
      
      // Show success toast
      setPopupMessage("Offers refreshed successfully!");
      setMessageType('success');
      
      // Clear toast after 3 seconds
      setTimeout(() => {
        setPopupMessage(null);
      }, 3000);
    } catch (error) {
      // Show error toast
      setPopupMessage("Failed to refresh offers. Please try again.");
      setMessageType('error');
      
      // Clear toast after 3 seconds
      setTimeout(() => {
        setPopupMessage(null);
      }, 3000);
    } finally {
      clearOffersRefreshPendingFlag();
    }
  };


  const openMakeOfferModal = (value: boolean, message: string = "", type: 'success' | 'error' | 'info' = 'success') => {
    if(isConnected){
      setMakeOfferModalOpen(value);
      setMessageType(type);
      setPopupMessage(message);
      
      // Automatically refresh offers when an offer is successfully submitted
      if (type === 'success' && selectedNFT) {
        const refreshOffers = async () => {
          try {
            const offers = await nftService.getOffersByNFT(selectedNFT.contract, selectedNFT.identifier, true);
            const convertedOffers = offers.map(offer => ({
              current_price: offer.current_price,
              expiration_time: offer.expiration_time,
              offerer: typeof offer.maker === 'string' ? offer.maker : offer.maker?.address || ''
            }));
            setSelectedNFTOffers(convertedOffers);
          } catch (error) {
            // Silent error handling for auto-refresh
          }
        };
        
        // Refresh offers after a delay to allow the offer to be processed
        setTimeout(refreshOffers, 3000);
      }
    }
    else {
      open({ view : 'Connect' });
    }
  }

  /*const handleBuyPrint = () => {
    navigate(`/nft/${routeParams.name}/print`);
  }*/

  return (
    <>
      <ImageOverlay
        isOpen={isImageOverlayOpen}
        onClose={() => setIsImageOverlayOpen(false)}
        imageUrl={selectedNFT?.image_url || ''}
        imageName={selectedNFT?.name || ''}
      />
      {isMakeOfferModalOpen && selectedNFT && 
        <MakeOffer 
          onClose={(isOpen, message, type) => openMakeOfferModal(isOpen, message || "", type as 'success' | 'error' || 'success')} 
          selectedNFT={selectedNFT} 
        />
      }
      <div className="pictureWrapper">
        {loading ? (
          <ColorRing
            visible={true}
            height="160"
            width="160"
            ariaLabel="color-ring-loading"
            wrapperStyle={{
              marginTop: '100px',
              marginBottom: '100px'
            }}
            wrapperClass="color-ring-wrapper"
            colors={['#e15b64', '#f47e60', '#f8b26a', '#abbd81', '#849b87']}
          />
        ) : (
          <div className="mainContainer">
            <div className="pictureInfoContent">
              <PicturePageHeader />
              <div className="pictureContainer-img">
                <div 
                  className="image-wrapper"
                  onMouseEnter={handleMouseEnter}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >
                  <img
                    className="picture"
                    key={selectedNFT?.name}
                    src={selectedNFT?.image_url}
                    alt="nft"
                    onClick={() => setIsImageOverlayOpen(true)}
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="magnify-lens"></div>
                  <div className="magnify-icon" onClick={() => setIsImageOverlayOpen(true)}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                    </svg>
                  </div>
                </div>
              </div>
              <div className="offersAndInfoContainer">
                <div className="pictureContainer gradient">
                  <div className="nftTextInfo">
                    <h1>{selectedNFT?.name.replace(/^#\d+\s*/, '')}</h1>
                    <p className="nftDescription">{selectedNFT?.description}</p>
                    <h4 className="nftDescription">Owner:</h4>
                    {ownerLoading ? (
                      <OwnerPlaceholder />
                    ) : (
                      <AddressLink showAddress={ensName ?? selectedNFTOwner} address={selectedNFTOwner}/>
                    )}
                    <div className="externalLinkContainer">
                      <button className="externalLinkButton">
                        <Link
                          className="externalLink"
                          to={etherscanURL ? `https://etherscan.io/address/${selectedNFT?.contract}` : `https://etherscan.io/nft/${selectedNFT?.contract}/${selectedNFT?.identifier}`}
                          target="_blank"
                        >
                          <img src={etherscanIcon} alt="etherscan icon" className="etherscan-icon" />
                        </Link>
                      </button>
                      <button className="externalLinkButton">
                        <Link
                          className="externalLink"
                          to={`https://opensea.io/assets/ethereum/${selectedNFT?.contract}/${selectedNFT?.identifier}`}
                          target="_blank"
                        >
                          <img src={openseaIcon} alt="opensea icon" />
                        </Link>
                      </button>            
                    </div>
                  </div>
                  <div className="pictureButtonContainer">
                    <button className="buyPrintButton"
                      onClick={() => openMakeOfferModal(true, "", messageType)}>
                      Make NFT Offer
                    </button>
                    <div className="card-footer-line"></div>
                  </div>

                  {popupMessage &&
                    <Popup message={popupMessage} messageType={messageType} />
                  }
                </div>
                <OfferList 
                  offers={selectedNFTOffers || []}
                  refreshButton={
                    <button 
                      className="refresh-button" 
                      onClick={handleRefreshOffers}
                      disabled={offersRefreshRateLimit.current.pending}
                      title="Refresh offers"
                    >
                      ⟳
                    </button>
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PicturePage;
